import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import {
	Building2,
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	ChevronsRight,
	Check,
	Copy,
	EyeOff,
	ExternalLink,
	Link2,
	Minus,
	MoreVertical,
	Pencil,
	RefreshCw,
	Trash2
} from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import { Pill } from '~/components/pill';
import { SortableHeader } from '~/components/grid/sortable-header';
import { ProductThumb } from './product-thumb';
import { StockPill } from './stock-pill';
import { cn } from '~/lib/utils';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';
import type { GridSort } from '~/lib/grid/sort';
import type { Density } from './grid-chrome';
import type { ProductGridRow } from '~/lib/fixtures/demo-products';
import { PRODUCT_COLUMN_KEYS, PRODUCT_SORT_SELECTORS, type ColumnVisibility, type ProductColumnKey } from '~/hooks/use-column-visibility';

export type RowAction = 'open' | 'quickEdit' | 'warehouses' | 'related' | 'sync' | 'duplicate' | 'delete';

const PAGE_SIZES = [25, 50, 100];

/**
 * Sticky-column layout (mirrors SystemGrid): the leading identity cluster (selection + thumb +
 * name) is a SINGLE pinned-left cell and the row menu a SINGLE pinned-right cell, with NO grid
 * gap — horizontal spacing comes from each cell's own `px-3`. A single pinned cell per side keeps
 * the frozen region seam-free during horizontal scroll.
 */
const IDENTITY_MIN = 260;
const MENU_W = 48;

/**
 * Width (px) + alignment for every toggleable column except `image` (lives in the identity cell).
 * `flex` marks text-bearing columns that share the horizontal slack on wide screens
 * (track becomes `minmax(width px, flex fr)`); the px width stays the floor for the
 * horizontal-scroll min-width. Code/number/toggle columns stay fixed.
 */
const COLUMN_META: Record<Exclude<ProductColumnKey, 'image'>, { width: number; align: 'left' | 'right' | 'center'; flex?: number }> = {
	sku: { width: 120, align: 'left' },
	barcode: { width: 150, align: 'left' },
	category: { width: 170, align: 'left', flex: 1 },
	stock: { width: 100, align: 'right' },
	preDiscount: { width: 120, align: 'right' },
	discount: { width: 100, align: 'right' },
	retail: { width: 110, align: 'right' },
	visible: { width: 80, align: 'center' },
	id: { width: 90, align: 'right' },
	subTitle: { width: 210, align: 'left', flex: 1 },
	baseName: { width: 190, align: 'left', flex: 1 },
	altCode: { width: 130, align: 'left' },
	groupCode: { width: 130, align: 'left' },
	erpCode: { width: 160, align: 'left' },
	manufacturer: { width: 170, align: 'left', flex: 1 },
	subType: { width: 150, align: 'left' },
	productType: { width: 130, align: 'left' },
	availability: { width: 150, align: 'left', flex: 1 },
	featured: { width: 100, align: 'center' },
	hasDiscount: { width: 120, align: 'center' },
	releaseDate: { width: 140, align: 'left' },
	sortIndex: { width: 90, align: 'right' }
};

/** Toggleable columns rendered after the identity cell (everything except `image`). */
const REST_KEYS = PRODUCT_COLUMN_KEYS.filter((k): k is Exclude<ProductColumnKey, 'image'> => k !== 'image');

/**
 * Builds the inline `grid-template-columns` plus the row's `minWidth` (so the table overflows
 * into horizontal scroll). Hidden columns collapse their track instead of leaving a gap.
 */
function buildLayout(visible: ColumnVisibility): { template: string; minWidth: number } {
	// Identity (selection + thumb + name) keeps the lion's share of slack; flexible text
	// columns split the rest proportionally instead of starving at their fixed width.
	const tracks: string[] = [`minmax(${IDENTITY_MIN}px,3fr)`];
	let minWidth = IDENTITY_MIN;
	for (const key of REST_KEYS) {
		if (!visible[key]) continue;
		const meta = COLUMN_META[key];
		tracks.push(meta.flex ? `minmax(${meta.width}px,${meta.flex}fr)` : `${meta.width}px`);
		minWidth += meta.width;
	}
	tracks.push(`${MENU_W}px`); // row menu
	minWidth += MENU_W;
	return { template: tracks.join(' '), minWidth };
}

function formatPrice(value: number | null): string {
	if (value === null || value === undefined) return '—';
	return `${value.toFixed(2)} €`;
}

/** Alignment utility for a toggleable cell wrapper. */
function alignClass(align: 'left' | 'right' | 'center'): string {
	if (align === 'right') return 'text-right';
	if (align === 'center') return 'flex justify-center';
	return 'min-w-0';
}

/** Green check (on) / muted dash (off) badge — used by the featured/has-discount columns. */
function BoolBadge({ on }: { on: boolean }) {
	return on ? (
		<span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-success text-success-fg">
			<Check size={13} />
		</span>
	) : (
		<span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-muted text-foreground-subtle">
			<Minus size={13} />
		</span>
	);
}

/** Max distinct category names shown inline before collapsing to "+N". */
const CATEGORY_DISPLAY_LIMIT = 3;

/**
 * Renders the distinct category names (already de-duped in the row mapper). Shows the
 * first {@link CATEGORY_DISPLAY_LIMIT} joined by " / " (Greek-uppercased), plus a "+N"
 * suffix when there are more. The `title` carries the full distinct list.
 */
function CategoryCell({ names }: { names: string[] }) {
	const { t } = useTranslation();
	if (names.length === 0) {
		return <span className="block truncate text-xs text-foreground-muted">—</span>;
	}
	const shown = names.slice(0, CATEGORY_DISPLAY_LIMIT);
	const extra = names.length - shown.length;
	const fullTitle = names.map(toGreekCaps).join(' / ');
	return (
		<span className="block truncate text-xs text-foreground-muted" title={fullTitle}>
			{toGreekCaps(shown.join(' / '))}
			{extra > 0 && <span className="text-foreground-subtle"> {t('products.grid.categoryMore', { count: extra })}</span>}
		</span>
	);
}

interface CellContext {
	density: Density;
	productTypeLabel: (type: number | null) => string;
	availabilityLabel: (status: number | null) => string;
}

/** Renders the inner content for a toggleable column (the wrapper handles width/alignment). */
function renderCell(key: Exclude<ProductColumnKey, 'image'>, row: ProductGridRow, ctx: CellContext) {
	const muted = (text?: string | null) => <span className="block truncate text-xs text-foreground-muted">{text || '—'}</span>;
	const mono = (text?: string | null) => (
		<span className="block truncate font-mono text-xs tabular-nums text-foreground-muted">{text || '—'}</span>
	);
	switch (key) {
		case 'sku':
			return <span className="block truncate font-mono text-xs font-medium tabular-nums text-foreground">{row.sku || '—'}</span>;
		case 'barcode':
			return (
				<span className="block truncate font-mono text-xs tabular-nums text-foreground-muted" title={row.barcode ?? undefined}>
					{row.barcode || '—'}
				</span>
			);
		case 'category':
			return <CategoryCell names={row.categoryNames} />;
		case 'stock':
			return <StockPill quantity={row.quantity} tone={row.stockTone} />;
		case 'preDiscount':
			return (
				<span className={cn('text-sm tabular-nums', row.preDiscountPrice === null ? 'text-foreground-subtle' : 'text-foreground')}>
					{formatPrice(row.preDiscountPrice)}
				</span>
			);
		case 'discount':
			return row.discountPercent !== null && row.discountPercent > 0 ? (
				<Pill variant="primary">−{Math.round(row.discountPercent)}%</Pill>
			) : (
				<span className="text-sm text-foreground-subtle">—</span>
			);
		case 'retail':
			return <span className="text-sm font-semibold tabular-nums text-foreground">{formatPrice(row.retailPrice)}</span>;
		case 'visible':
			return row.isVisible ? (
				<span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-success text-success-fg">
					<Check size={13} />
				</span>
			) : (
				<span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-muted text-foreground-subtle">
					<EyeOff size={13} />
				</span>
			);
		case 'id':
			return <span className="font-mono text-xs tabular-nums text-foreground-muted">#{row.id}</span>;
		case 'subTitle':
			return muted(row.subTitle);
		case 'baseName':
			return muted(row.baseName);
		case 'altCode':
			return mono(row.altCode);
		case 'groupCode':
			return mono(row.groupCode);
		case 'erpCode':
			return mono(row.erpCode);
		case 'manufacturer':
			return muted(row.manufacturerName);
		case 'subType':
			return muted(row.subTypeName);
		case 'productType':
			return <span className="block truncate text-xs text-foreground-muted">{ctx.productTypeLabel(row.productType)}</span>;
		case 'availability':
			return (
				<span className="block truncate text-xs text-foreground-muted">
					{row.availabilityName || ctx.availabilityLabel(row.availabilityStatus)}
				</span>
			);
		case 'featured':
			return <BoolBadge on={Boolean(row.isFeatured)} />;
		case 'hasDiscount':
			return <BoolBadge on={Boolean(row.hasDiscount)} />;
		case 'releaseDate':
			return (
				<span className="whitespace-nowrap text-xs tabular-nums text-foreground-muted">
					{row.releaseDate ? format(new Date(row.releaseDate), 'dd MMM yyyy', { locale: el }) : '—'}
				</span>
			);
		case 'sortIndex':
			return <span className="text-xs tabular-nums text-foreground-muted">{row.sortIndex ?? '—'}</span>;
	}
}

/** Native tri-state header checkbox (matches the approved HTML's styled `.check`). */
function HeaderCheckbox({
	checked,
	indeterminate,
	onChange,
	label
}: {
	checked: boolean;
	indeterminate: boolean;
	onChange: (v: boolean) => void;
	label: string;
}) {
	const ref = useRef<HTMLInputElement>(null);
	useEffect(() => {
		if (ref.current) ref.current.indeterminate = indeterminate && !checked;
	}, [indeterminate, checked]);
	return (
		<input
			ref={ref}
			type="checkbox"
			aria-label={label}
			checked={checked}
			onChange={e => onChange(e.target.checked)}
			className="h-4 w-4 flex-shrink-0 cursor-pointer accent-brand"
		/>
	);
}

interface GridTableProps {
	rows: ProductGridRow[];
	loading: boolean;
	density: Density;
	visibleColumns: ColumnVisibility;
	productTypeLabel: (type: number | null) => string;
	selectedIds: Set<number>;
	onToggleRow: (id: number, checked: boolean) => void;
	onToggleAll: (checked: boolean) => void;
	onRowAction: (row: ProductGridRow, action: RowAction) => void;
	// Server-side sort
	sort: GridSort | null;
	onSort: (key: string) => void;
	// Server-side pagination
	pageIndex: number;
	pageSize: number;
	total: number;
	onPageChange: (pageIndex: number) => void;
	onPageSizeChange: (pageSize: number) => void;
}

/** Builds a compact page-number list with ellipses (1 … current-1 current current+1 … last). */
function pageNumbers(current: number, pageCount: number): (number | 'gap')[] {
	if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i);
	const out: (number | 'gap')[] = [0];
	const start = Math.max(1, current - 1);
	const end = Math.min(pageCount - 2, current + 1);
	if (start > 1) out.push('gap');
	for (let i = start; i <= end; i++) out.push(i);
	if (end < pageCount - 2) out.push('gap');
	out.push(pageCount - 1);
	return out;
}

export function GridTable({
	rows,
	loading,
	density,
	visibleColumns,
	productTypeLabel,
	selectedIds,
	onToggleRow,
	onToggleAll,
	onRowAction,
	sort,
	onSort,
	pageIndex,
	pageSize,
	total,
	onPageChange,
	onPageSizeChange
}: GridTableProps) {
	const { t } = useTranslation();
	const pageCount = Math.max(1, Math.ceil(total / pageSize));
	const cellPadY = density === 'compact' ? 'py-1.5' : 'py-2.5';

	// Dynamic grid template + min width — collapses tracks for hidden columns and overflows
	// into horizontal scroll once the visible columns exceed the viewport.
	const { template: gridTemplate, minWidth } = useMemo(() => buildLayout(visibleColumns), [visibleColumns]);

	// Toggleable columns after the identity cell, in config order, filtered to the visible set.
	const restVisible = useMemo(() => REST_KEYS.filter(k => visibleColumns[k]), [visibleColumns]);

	const allSelected = rows.length > 0 && rows.every(r => selectedIds.has(r.id));
	const someSelected = rows.some(r => selectedIds.has(r.id));
	const from = total === 0 ? 0 : pageIndex * pageSize + 1;
	const to = Math.min((pageIndex + 1) * pageSize, total);

	const cellCtx: CellContext = {
		density,
		productTypeLabel,
		availabilityLabel: status => (status != null ? t(`products.availability.${status}`, status.toString()) : '—')
	};

	// Sticky cell chrome: identity pinned left, row menu pinned right.
	const headLabel = 'text-[10px] font-semibold uppercase tracking-[0.04em] text-foreground-subtle';
	const stickyBodyBg = (selected: boolean) => (selected ? 'bg-brand-subtle' : 'bg-surface group-hover:bg-elevated');

	const sortDirFor = (key: string) => (sort?.key === key ? sort.dir : null);
	const onSortIf = (key: string, sortable: boolean) => (sortable ? () => onSort(key) : undefined);

	return (
		<div>
			<div className="overflow-x-auto rounded-b-md border border-border bg-surface">
				{/* header row */}
				<div className="grid items-center border-b border-border bg-elevated py-2" style={{ gridTemplateColumns: gridTemplate, minWidth }}>
					<span className="sticky left-0 z-[1] flex items-center gap-2 border-r border-border bg-elevated px-3">
						<HeaderCheckbox
							checked={allSelected}
							indeterminate={someSelected}
							onChange={onToggleAll}
							label={t('products.grid.selectAll')}
						/>
						<span className={headLabel}>
							<SortableHeader
								label={t('products.grid.columns.name')}
								active={sortDirFor('name')}
								onSort={onSortIf('name', Boolean(PRODUCT_SORT_SELECTORS.name))}
							/>
						</span>
					</span>
					{restVisible.map(key => {
						const meta = COLUMN_META[key];
						return (
							<span
								key={key}
								className={cn('px-3', headLabel, meta.align === 'right' && 'text-right', meta.align === 'center' && 'text-center')}>
								<SortableHeader
									label={t(`products.grid.columns.${key}`)}
									align={meta.align}
									active={sortDirFor(key)}
									onSort={onSortIf(key, Boolean(PRODUCT_SORT_SELECTORS[key]))}
								/>
							</span>
						);
					})}
					<span className="sticky right-0 z-[1] border-l border-border bg-elevated px-3" />
				</div>

				{/* body */}
				{loading
					? Array.from({ length: 8 }).map((_, i) => (
							<div
								key={i}
								className={cn('grid items-center border-b border-border-subtle', cellPadY)}
								style={{ gridTemplateColumns: gridTemplate, minWidth }}>
								<span className="sticky left-0 z-[1] flex items-center gap-2 border-r border-border bg-surface px-3">
									<span className="h-4 w-4 animate-pulse rounded bg-muted" />
									{visibleColumns.image && <span className="h-[52px] w-10 animate-pulse rounded bg-muted" />}
									<span className="h-4 w-3/4 flex-1 animate-pulse rounded bg-muted" />
								</span>
								{restVisible.map(key => (
									<span key={key} className="px-3">
										<span className="block h-4 w-full animate-pulse rounded bg-muted" />
									</span>
								))}
								<span className="sticky right-0 z-[1] border-l border-border bg-surface px-3" />
							</div>
						))
					: rows.length === 0
						? null
						: rows.map(row => {
								const selected = selectedIds.has(row.id);
								const stickyBg = stickyBodyBg(selected);
								return (
									<div
										key={row.id}
										style={{ gridTemplateColumns: gridTemplate, minWidth }}
										className={cn(
											'group grid items-center border-b border-border-subtle',
											cellPadY,
											selected ? 'bg-brand-subtle' : 'hover:bg-elevated'
										)}>
										{/* Identity — selection + thumb + name (pinned left) */}
										<div className={cn('flex items-center gap-2 sticky left-0 z-[1] border-r border-border px-3', stickyBg)}>
											<input
												type="checkbox"
												aria-label={t('products.grid.selectRow', { name: row.name })}
												checked={selected}
												onChange={e => onToggleRow(row.id, e.target.checked)}
												className="h-4 w-4 flex-shrink-0 cursor-pointer accent-brand"
											/>
											{visibleColumns.image && <ProductThumb src={row.imageUrl} alt={row.name} compact={density === 'compact'} />}
											<div className="min-w-0 flex-1">
												<Link
													to={`/products/${row.id}`}
													className="line-clamp-2 text-sm font-medium leading-snug text-brand-subtle-fg hover:underline">
													{row.name}
												</Link>
												<p className="mt-0.5 truncate text-[11px] text-foreground-muted">
													{[productTypeLabel(row.productType), row.categoryLead && toGreekCaps(row.categoryLead)]
														.filter(Boolean)
														.join(' · ')}
												</p>
											</div>
										</div>

										{/* Toggleable columns */}
										{restVisible.map(key => (
											<span key={key} className={cn('px-3 min-w-0', alignClass(COLUMN_META[key].align))}>
												{renderCell(key, row, cellCtx)}
											</span>
										))}

										{/* Row menu (pinned right) */}
										<span className={cn('flex justify-end sticky right-0 z-[1] border-l border-border px-3', stickyBg)}>
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button variant="ghost" size="icon" type="button" aria-label={t('products.grid.rowMenu.label')}>
														<MoreVertical size={16} />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end" className="w-56">
													<DropdownMenuItem onSelect={() => onRowAction(row, 'open')}>
														<ExternalLink size={14} /> {t('products.grid.rowMenu.open')}
													</DropdownMenuItem>
													<DropdownMenuItem onSelect={() => onRowAction(row, 'quickEdit')}>
														<Pencil size={14} /> {t('products.grid.rowMenu.quickEdit')}
													</DropdownMenuItem>
													<DropdownMenuSeparator />
													<DropdownMenuItem onSelect={() => onRowAction(row, 'warehouses')}>
														<Building2 size={14} /> {t('products.grid.rowMenu.warehouses')}
													</DropdownMenuItem>
													<DropdownMenuItem onSelect={() => onRowAction(row, 'related')}>
														<Link2 size={14} /> {t('products.grid.rowMenu.related')}
													</DropdownMenuItem>
													<DropdownMenuItem onSelect={() => onRowAction(row, 'sync')}>
														<RefreshCw size={14} /> {t('products.grid.rowMenu.sync')}
													</DropdownMenuItem>
													<DropdownMenuSeparator />
													<DropdownMenuItem onSelect={() => onRowAction(row, 'duplicate')}>
														<Copy size={14} /> {t('products.grid.rowMenu.duplicate')}
													</DropdownMenuItem>
													<DropdownMenuItem variant="destructive" onSelect={() => onRowAction(row, 'delete')}>
														<Trash2 size={14} /> {t('products.grid.rowMenu.delete')}
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</span>
									</div>
								);
							})}
			</div>

			{/* Pager */}
			<div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2 text-xs text-foreground-muted">
				<div className="flex items-center gap-2">
					<span>{t('products.pager.rowsPerPage')}</span>
					<Select value={String(pageSize)} onValueChange={v => onPageSizeChange(Number(v))}>
						<SelectTrigger className="h-8 w-20">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{PAGE_SIZES.map(size => (
								<SelectItem key={size} value={String(size)}>
									{size}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-3">
					<span>
						<strong className="font-medium tabular-nums text-foreground">
							{from}–{to}
						</strong>{' '}
						{t('products.gridBar.of')} <strong className="font-medium tabular-nums text-foreground">{total.toLocaleString('el-GR')}</strong>
					</span>
					<div className="flex items-center gap-1">
						<Button
							variant="outline"
							size="icon"
							type="button"
							disabled={pageIndex === 0}
							onClick={() => onPageChange(0)}
							aria-label={t('products.pager.first')}>
							<ChevronsLeft size={15} />
						</Button>
						<Button
							variant="outline"
							size="icon"
							type="button"
							disabled={pageIndex === 0}
							onClick={() => onPageChange(pageIndex - 1)}
							aria-label={t('products.pager.prev')}>
							<ChevronLeft size={15} />
						</Button>
						{pageNumbers(pageIndex, pageCount).map((p, i) =>
							p === 'gap' ? (
								<span key={`gap-${i}`} className="px-1 text-foreground-subtle">
									…
								</span>
							) : (
								<Button
									key={p}
									variant={p === pageIndex ? 'default' : 'outline'}
									size="icon"
									type="button"
									onClick={() => onPageChange(p)}
									aria-label={t('products.pager.page', { page: p + 1 })}
									aria-current={p === pageIndex ? 'page' : undefined}>
									{p + 1}
								</Button>
							)
						)}
						<Button
							variant="outline"
							size="icon"
							type="button"
							disabled={pageIndex >= pageCount - 1}
							onClick={() => onPageChange(pageIndex + 1)}
							aria-label={t('products.pager.next')}>
							<ChevronRight size={15} />
						</Button>
						<Button
							variant="outline"
							size="icon"
							type="button"
							disabled={pageIndex >= pageCount - 1}
							onClick={() => onPageChange(pageCount - 1)}
							aria-label={t('products.pager.last')}>
							<ChevronsRight size={15} />
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}
